import React, { useState, useEffect, useCallback, useRef } from "react";
import useFetch from "../../../hooks/useFetch";

const usePDIDSHook  = () => {
    const [fetchData] = useFetch();

    const [creators, setCreators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [registering, setRegistering] = useState(false);
    const [registerError, setRegisterError] = useState("");
    const [registerSuccess, setRegisterSuccess] = useState("");

    const debounceRef = useRef(null);
    const hasFetchedRef = useRef(false);

    // Fetch / Search Creators
    const fetchCreators = useCallback(
        async (search = "") => {
            try {
                setLoading(true);
                setError("");

                const config = {
                    url: "/ids",
                    method: "get",
                    params: { search },
                };

                const response = await fetchData(config, { silent: true });

                if (response?.status) {
                    setCreators(response.data || []);
                } else {
                    setCreators([]);
                    setError(response?.message || "Failed to load creators.");
                }
            } catch (err) {
                console.error("Fetch Creators Error:", err);
                setCreators([]);
                setError("Failed to load creators.");
            } finally {
                setLoading(false);
            }
        },
        [fetchData]
    );

    // Debounced Search
    const searchCreators = useCallback(
        (term) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
                fetchCreators(term);
            }, 350);
        },
        [fetchCreators]
    );

    // Register Creator
    const registerCreator = useCallback(
        async ({ focus, lang, channelLink }, accessToken = null) => {
            try {
                setRegistering(true);
                setRegisterError("");
                setRegisterSuccess("");

                const config = {
                    url: "/ids/register",
                    method: "post",
                    data: {
                        focus,
                        lang,
                        channelLink,
                    },
                    headers: accessToken
                        ? {
                              Authorization: `Bearer ${accessToken}`,
                          }
                        : {},
                };

                const response = await fetchData(config);

                if (response?.status) {
                    setRegisterSuccess(
                        response.message || "Registered successfully!"
                    );

                    if (response.data) {
                        setCreators((prev) => [response.data, ...prev]);
                    }

                    return {
                        success: true,
                        data: response.data,
                    };
                }

                const message =
                    response?.message ||
                    "Registration failed. Please try again.";

                setRegisterError(message);

                return {
                    success: false,
                    message,
                };
            } catch (err) {
                console.error("Register Creator Error:", err);

                const message = "Registration failed. Please try again.";

                setRegisterError(message);

                return {
                    success: false,
                    message,
                };
            } finally {
                setRegistering(false);
            }
        },
        [fetchData]
    );

    useEffect(() => {
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchCreators();
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [fetchCreators]);

    return {
        creators,
        loading,
        error,

        fetchCreators,
        searchCreators,

        registerCreator,
        registering,
        registerError,
        registerSuccess,

        setRegisterError,
        setRegisterSuccess,
    };
};

export default usePDIDSHook ;